import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpenText, ClipboardCheck, FileText, GraduationCap, HeartHandshake, Newspaper, NotebookPen, Target, Trash2 } from 'lucide-react';
import { Badge, Button, ConfirmDialog, EmptyState, LoadingBlock, Modal, Note, Tabs, useToast } from '@/components/ui';
import { useAuth } from '@/auth/AuthProvider';
import { ApiError, api } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import type { DraftDiscardResult, DraftItem, DraftKind, DraftsResponse } from '@/lib/types';
import { DRAFTS_QUERY_KEY } from '@/lib/use-drafts-count';

/**
 * «المسودات» (0119 — طلبُ المراكز 1 نوفمبر 2026): نافذةٌ واحدة تجمع كلَّ مسوداتي من كل نوع، وللإدارة والمشرف والمفوَّض
 * تبويبٌ ثانٍ بمسودات الكادر (من الأنواع التي يقرؤونها أصلاً). الحذفُ إخفاءٌ بأثر — السجلُّ لا يُمحى والمسودةُ ليست سجلاً بعد —
 * و`ConfirmDialog` يسمّي ما سيُحذف، والنتيجةُ لكل بندٍ وحده (سقوطُ واحدٍ لا يُسقط البقية). النطاقُ والصلاحيةُ في الخادم؛ هنا عرضٌ.
 */
const KIND_ICON: Record<DraftKind, typeof FileText> = {
  form: ClipboardCheck, plan: Target, counseling_plan: HeartHandshake, preparation: NotebookPen, report: FileText, news: Newspaper, academy_post: BookOpenText, course: GraduationCap,
};
const KIND_LABEL: Record<DraftKind, string> = {
  form: 'نموذج', plan: 'خطة تربوية', counseling_plan: 'خطة إرشادية', preparation: 'تحضير يومي', report: 'تقرير', news: 'خبر', academy_post: 'منشور أكاديمية', course: 'دورة',
};
const KIND_ORDER: DraftKind[] = ['form', 'plan', 'counseling_plan', 'preparation', 'report', 'news', 'academy_post', 'course'];

const describe = (d: DraftItem) => `${KIND_LABEL[d.kind]} «${d.titleAr}»${d.studentName ? ` لـ${d.studentName}` : ''}`;

function useDiscard(onDone?: () => void) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (items: Array<{ kind: DraftKind; id: string }>) => api.post<DraftDiscardResult>('/me/drafts/discard', { items }),
    onSuccess: async (r, items) => {
      await qc.invalidateQueries({ queryKey: DRAFTS_QUERY_KEY });
      // قوائمُ الأنواع نفسِها تتغيّر — فتُبطَل كلُّها لا واحدةٌ بعينها
      await qc.invalidateQueries();
      const failed = r.results.filter((x) => !x.ok);
      const done = r.results.length - failed.length;
      if (done) toast.push('success', done === 1 && items.length === 1 ? 'حُذفت المسودة — ويبقى أثرُ الحذف في سجل التدقيق.' : `حُذفت ${done} من ${r.results.length} مسودة.`);
      for (const f of failed) toast.push('error', f.messageAr ?? (f.code === 'NOT_FOUND' ? 'المسودة غير موجودة في نطاقك.' : 'تعذّر الحذف.'));
      onDone?.();
    },
    onError: (e) => toast.push('error', e instanceof ApiError ? e.message : 'تعذّر الحذف.'),
  });
}

export function DraftsPanel({ onNavigate }: { onNavigate?: () => void }) {
  const { can, institutionId } = useAuth();
  const navigate = useNavigate();
  const staffScope = !!institutionId && can('draft.institution.view');
  const [scope, setScope] = useState<'mine' | 'institution'>('mine');
  const [kind, setKind] = useState<DraftKind | 'ALL'>('ALL');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<DraftItem[] | null>(null);
  const q = useQuery({
    queryKey: [...DRAFTS_QUERY_KEY, scope, institutionId],
    queryFn: () => api.get<DraftsResponse>(`/me/drafts?scope=${scope}`),
  });
  const discard = useDiscard(() => { setSelected(new Set()); setConfirm(null); });
  const items = useMemo(() => q.data?.items ?? [], [q.data]);
  const rows = useMemo(() => (kind === 'ALL' ? items : items.filter((d) => d.kind === kind)), [items, kind]);
  const present = KIND_ORDER.filter((k) => items.some((d) => d.kind === k));
  const key = (d: DraftItem) => `${d.kind}:${d.id}`;
  const chosen = rows.filter((d) => selected.has(key(d)) && d.canDiscard);
  const open = (d: DraftItem) => { onNavigate?.(); navigate(d.path); };

  return (
    <div className="stack gap-3">
      {staffScope && (
        <Tabs
          value={scope}
          onChange={(v) => { setScope(v); setKind('ALL'); setSelected(new Set()); }}
          tabs={[{ id: 'mine', label: 'مسوداتي' }, { id: 'institution', label: 'مسودات الكادر' }]}
        />
      )}
      <p className="caption" style={{ marginBlockStart: 0 }}>
        {scope === 'mine'
          ? 'كلُّ ما بدأتَه ولم تُنهِه بعد — من كل نوع، في مكانٍ واحد. افتحه لتكمله، أو احذفه إن لم تعد تريده.'
          : 'مسوداتُ الكادر في مؤسستك من الأنواع التي تقرؤها. ما تحذفه يختفي عن كاتبه أيضاً ويبقى أثرُ الحذف باسمك.'}
      </p>
      {q.isLoading && <LoadingBlock />}
      {q.isError && <Note kind="danger">{(q.error as ApiError).message}</Note>}
      {q.isSuccess && items.length === 0 && (
        <EmptyState art="file" title={scope === 'mine' ? 'لا مسودات لديك' : 'لا مسودات لدى الكادر'} text={scope === 'mine' ? 'كلُّ ما تبدؤه ولا تُنهيه يظهر هنا بمجرّد حفظه مسودةً.' : 'حين يبدأ أحدٌ من الكادر نموذجاً أو خطةً ولا يُنهيها تظهر هنا.'} />
      )}
      {present.length > 1 && (
        <div className="row gap-2 wrap" role="group" aria-label="فرز بالنوع">
          {(['ALL', ...present] as const).map((k) => {
            const on = kind === k;
            const n = k === 'ALL' ? items.length : (q.data?.counts[k] ?? 0);
            return <button key={k} type="button" className={`chip chip--btn${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => setKind(k)}>{k === 'ALL' ? 'الكل' : KIND_LABEL[k]} <span className="num">({n})</span></button>;
          })}
        </div>
      )}
      {rows.length > 0 && (
        <ul className="stack gap-2" style={{ listStyle: 'none', padding: 0, margin: 0 }} aria-label="المسودات">
          {rows.map((d) => {
            const Icon = KIND_ICON[d.kind];
            const k = key(d);
            return (
              <li key={k} className="row gap-3 wrap card card--soft" style={{ padding: 'var(--s-3)', alignItems: 'center' }}>
                {d.canDiscard && <input type="checkbox" aria-label={`حدّد ${describe(d)}`} checked={selected.has(k)} onChange={(e) => setSelected((s) => { const n = new Set(s); if (e.target.checked) n.add(k); else n.delete(k); return n; })} />}
                <Icon size={18} aria-hidden style={{ color: 'var(--primary-700)' }} />
                <div className="grow stack" style={{ minWidth: 0 }}>
                  <strong className="truncate"><Link to={d.path} onClick={onNavigate}>{d.titleAr}</Link></strong>
                  <span className="caption">
                    <Badge tone="neutral">{KIND_LABEL[d.kind]}</Badge>
                    {d.studentName ? ` · ${d.studentName}` : ''}{d.studentRef ? ` (${d.studentRef})` : ''}
                    {scope === 'institution' ? ` · ${d.authorName}` : ''}
                    {' · '}<span className="num">{fmtDateTime(d.updatedAt)}</span>
                  </span>
                </div>
                <div className="row gap-2">
                  <Button size="sm" variant="secondary" onClick={() => open(d)}>افتح</Button>
                  <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} disabled={!d.canDiscard} disabledReason={!d.canDiscard ? 'تحذف مسودتَك أنت — ومسودةُ غيرك تحذفها الإدارة.' : undefined} onClick={() => setConfirm([d])}>احذف</Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {chosen.length > 0 && (
        <div className="row gap-2 wrap">
          <Button variant="danger" icon={<Trash2 size={15} />} onClick={() => setConfirm(chosen)}>{`احذف المحدَّد (${chosen.length})`}</Button>
          <Button variant="ghost" onClick={() => setSelected(new Set())}>ألغِ التحديد</Button>
        </div>
      )}
      {confirm && (
        <ConfirmDialog
          title={confirm.length === 1 ? 'حذف المسودة' : `حذف ${confirm.length} مسودات`}
          impact={`ستُحذف ${confirm.length === 1 ? 'مسودة' : `${confirm.length} مسودات`}: ${confirm.map(describe).join('، ')} — لن تظهر بعدها في أي مكان، ويبقى أثرُ الحذف في سجل التدقيق.`}
          confirmLabel={confirm.length === 1 ? 'احذف المسودة' : 'احذف المسودات'}
          loading={discard.isPending}
          onConfirm={() => discard.mutate(confirm.map((d) => ({ kind: d.kind, id: d.id })))}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

/** النافذة — من زرّ «المسودات» في الترويسة؛ ورقةٌ من الأسفل على الجوال (تنسيقُ Modal نفسُه) */
export function DraftsDialog({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="المسودات" description="كلُّ ما لم تُنهِه بعد — افتحه أو احذفه." onClose={onClose} wide>
      <DraftsPanel onNavigate={onClose} />
    </Modal>
  );
}

/** زرّ «احذف المسودة» في شاشة المسودة نفسِها — المسارُ نفسُه ببندٍ واحد، ثم العودةُ إلى حيث جاء */
export function DiscardDraftButton({ kind, id, titleAr, studentName, backTo }: { kind: DraftKind; id: string; titleAr: string; studentName?: string | null; backTo: string }) {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const discard = useDiscard(() => { setConfirm(false); navigate(backTo, { replace: true }); });
  const d = { kind, id, titleAr, studentName: studentName ?? null } as DraftItem;
  return (
    <>
      <Button variant="ghost" icon={<Trash2 size={15} />} onClick={() => setConfirm(true)}>احذف المسودة</Button>
      {confirm && (
        <ConfirmDialog
          title="حذف المسودة"
          impact={`ستُحذف ${describe(d)} — لن تظهر بعدها في أي مكان، ويبقى أثرُ الحذف في سجل التدقيق.`}
          confirmLabel="احذف المسودة"
          loading={discard.isPending}
          onConfirm={() => discard.mutate([{ kind, id }])}
          onClose={() => setConfirm(false)}
        />
      )}
    </>
  );
}
